import { useEffect, useState } from "react"
import { Link } from "react-router"
import useUser from "../hooks/useUser"
import {
  listApiKeys, createApiKey, revokeApiKey,
  listWebhooks, createWebhook, updateWebhook, deleteWebhook,
} from "../lib/api"
import useUpgrade from "../hooks/useUpgrade"

// Authenticated developer settings at /app/developers. Gated on
// `entitlements.api_access` — Pro, or a free account the :free_api_access flag
// is on for. Anyone else sees an upgrade prompt instead of the management UI
// (the backend 403s them too).
// Manages API keys and scan webhooks used against the public API
// (/api/public/v1). Secrets — a key's plaintext token and a webhook's signing
// secret — are shown exactly once, on creation.

const CARD = "border border-[color:var(--color-border)] dark:border-storm rounded-lg bg-white dark:bg-midnight p-5 mb-5"
const MUTED = "text-[color:var(--color-muted)] dark:text-muted-light"
const PRIMARY_BTN = "bg-voltage text-coal rounded-lg px-3 py-2 text-sm font-medium hover:bg-voltage-dark transition disabled:opacity-50"
const GHOST_BTN = "border border-[color:var(--color-border)] dark:border-storm text-ink dark:text-cloud rounded-lg px-3 py-2 text-sm font-medium hover:border-voltage transition disabled:opacity-50"
const INPUT = "w-full px-3 py-2 border border-[color:var(--color-border)] dark:border-storm rounded text-ink dark:text-cloud font-mono text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 dark:focus-visible:ring-offset-midnight"

function formatDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
}

export default function DevelopersPage() {
  const { status, user } = useUser()

  if (status === "loading" || (status !== "anonymous" && !user)) {
    return <div className={`max-w-2xl mx-auto py-8 px-4 text-center ${MUTED} text-sm`}>Loading…</div>
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-display font-semibold text-xl text-ink dark:text-cloud">Developers</h1>
        <Link to="/app" className="text-sm text-voltage hover:text-voltage-dark dark:hover:text-voltage-light transition">
          ← Your codes
        </Link>
      </div>

      {user?.entitlements?.api_access ? (
        <>
          <p className={`text-sm ${MUTED} mb-5`}>
            Build on TangoQR. Create codes, repoint them, and receive scan events from your own systems.{" "}
            <a href="/developers" className="text-voltage hover:text-voltage-dark dark:hover:text-voltage-light underline">
              Read the API docs
            </a>.
          </p>
          <ApiKeysSection />
          <WebhooksSection />
        </>
      ) : (
        <UpgradeGate />
      )}
    </div>
  )
}

// The API is on every plan (FreeApiAccess, on for everyone since 2026-09-23),
// so a free account sees this only while that door is shut: the flag is off
// for it, or the analytics gate is rolled back, which closes the door with it.
// The copy says that, not "the API is a Pro feature" (#336).
function UpgradeGate() {
  // The pricing box, not a direct monthly checkout (#336).
  const { upgrade } = useUpgrade()
  return (
    <section className={CARD}>
      <div className="text-xs uppercase tracking-wide text-plasma">API</div>
      <div className="font-display font-semibold text-ink dark:text-cloud mt-0.5">The API is off for free accounts right now</div>
      <p className={`text-sm ${MUTED} mt-1 mb-4`}>
        API keys, creating and repointing codes from your own systems, and a signed webhook on every scan.
        Free accounts normally get all of it. Pro has it today.
      </p>
      <button type="button" onClick={() => upgrade()} className={PRIMARY_BTN}>
        Upgrade to Pro
      </button>
    </section>
  )
}

// A one-time secret reveal: a copyable mono box shown once after creation.
function SecretReveal({ label, value, onDismiss }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* clipboard blocked */ }
  }
  return (
    <div className="border border-voltage rounded-lg bg-voltage-pale dark:bg-voltage/10 p-4 mb-4">
      <div className="text-xs font-semibold text-voltage-dark dark:text-voltage-light mb-1">{label}</div>
      <p className={`text-xs ${MUTED} mb-2`}>Copy it now — you won't be able to see it again.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate font-mono text-sm text-ink dark:text-cloud bg-white dark:bg-coal rounded px-2 py-1.5 border border-[color:var(--color-border)] dark:border-storm">
          {value}
        </code>
        <button type="button" onClick={copy} className={GHOST_BTN}>{copied ? "Copied" : "Copy"}</button>
        <button type="button" onClick={onDismiss} className={`text-sm ${MUTED} hover:text-ink dark:hover:text-cloud transition`}>Done</button>
      </div>
    </div>
  )
}

function ApiKeysSection() {
  const [keys, setKeys] = useState(null)
  const [error, setError] = useState(null)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState("")
  const [scope, setScope] = useState("read,write")
  const [revealed, setRevealed] = useState(null) // newly minted key incl. token
  const [busy, setBusy] = useState(false)

  useEffect(() => { reload() }, [])
  async function reload() {
    try { setKeys((await listApiKeys()).keys) } catch (e) { setError(e.message || "Couldn't load keys.") }
  }

  async function create(e) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const key = await createApiKey({ name: name.trim(), scopes: scope.split(",") })
      setRevealed(key)
      setName(""); setScope("read,write"); setCreating(false)
      await reload()
    } catch (e) { setError(e.message || "Couldn't create the key.") } finally { setBusy(false) }
  }

  async function revoke(id) {
    if (!window.confirm("Revoke this key? Any integration using it will stop working immediately.")) return
    setError(null)
    try { await revokeApiKey(id); await reload() } catch (e) { setError(e.message || "Couldn't revoke the key.") }
  }

  return (
    <section className={CARD}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-semibold text-ink dark:text-cloud">API keys</h2>
        {!creating && <button type="button" onClick={() => setCreating(true)} className={PRIMARY_BTN}>Create key</button>}
      </div>

      {error && <div className="border border-fail rounded p-3 text-fail text-sm mb-4">{error}</div>}
      {revealed && (
        <SecretReveal label={`Your new key “${revealed.name}”`} value={revealed.token} onDismiss={() => setRevealed(null)} />
      )}

      {creating && (
        <form onSubmit={create} className="border border-[color:var(--color-border)] dark:border-storm rounded-lg p-4 mb-4 space-y-3">
          <div>
            <label htmlFor="key-name" className="block text-sm font-medium text-ink dark:text-cloud mb-1">Name</label>
            <input id="key-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Production server" className={INPUT} />
          </div>
          <div>
            <label htmlFor="key-scope" className="block text-sm font-medium text-ink dark:text-cloud mb-1">Access</label>
            <select id="key-scope" value={scope} onChange={(e) => setScope(e.target.value)} className={INPUT}>
              <option value="read,write">Read &amp; write</option>
              <option value="read">Read-only</option>
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setCreating(false)} className={`text-sm ${MUTED} hover:text-ink dark:hover:text-cloud transition`}>Cancel</button>
            <button type="submit" disabled={busy} className={PRIMARY_BTN}>{busy ? "Creating…" : "Create key"}</button>
          </div>
        </form>
      )}

      {keys === null ? (
        <p className={`text-sm ${MUTED}`}>Loading…</p>
      ) : keys.length === 0 ? (
        <p className={`text-sm ${MUTED}`}>No keys yet. Create one to start calling the API.</p>
      ) : (
        <ul className="divide-y divide-[color:var(--color-border)] dark:divide-storm">
          {keys.map((k) => (
            <li key={k.id} className="py-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium text-ink dark:text-cloud truncate">{k.name}</div>
                <div className={`text-xs ${MUTED} font-mono`}>
                  {k.token_prefix}…&nbsp;·&nbsp;{k.scopes.join(" + ")}
                  {k.last_used_at ? ` · last used ${formatDate(k.last_used_at)}` : " · never used"}
                </div>
              </div>
              <button type="button" onClick={() => revoke(k.id)} className="border border-fail text-fail rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-fail/5 transition flex-shrink-0">
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function WebhooksSection() {
  const [hooks, setHooks] = useState(null)
  const [error, setError] = useState(null)
  const [creating, setCreating] = useState(false)
  const [url, setUrl] = useState("")
  const [revealed, setRevealed] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => { reload() }, [])
  async function reload() {
    try { setHooks((await listWebhooks()).webhooks) } catch (e) { setError(e.message || "Couldn't load webhooks.") }
  }

  async function create(e) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const hook = await createWebhook({ url: url.trim() })
      setRevealed(hook)
      setUrl(""); setCreating(false)
      await reload()
    } catch (e) { setError(e.message || "Couldn't add the endpoint.") } finally { setBusy(false) }
  }

  async function toggle(hook) {
    setError(null)
    try { await updateWebhook(hook.id, { enabled: !hook.enabled }); await reload() } catch (e) { setError(e.message || "Couldn't update the endpoint.") }
  }

  async function remove(id) {
    if (!window.confirm("Delete this endpoint? You'll stop receiving scan events at this URL.")) return
    setError(null)
    try { await deleteWebhook(id); await reload() } catch (e) { setError(e.message || "Couldn't delete the endpoint.") }
  }

  return (
    <section className={CARD}>
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-semibold text-ink dark:text-cloud">Webhooks</h2>
        {!creating && <button type="button" onClick={() => setCreating(true)} className={PRIMARY_BTN}>Add endpoint</button>}
      </div>
      <p className={`text-sm ${MUTED} mb-3`}>We POST a signed <span className="font-mono">scan.recorded</span> event to each endpoint on every scan.</p>

      {error && <div className="border border-fail rounded p-3 text-fail text-sm mb-4">{error}</div>}
      {revealed && (
        <SecretReveal label="Signing secret" value={revealed.secret} onDismiss={() => setRevealed(null)} />
      )}

      {creating && (
        <form onSubmit={create} className="border border-[color:var(--color-border)] dark:border-storm rounded-lg p-4 mb-4 space-y-3">
          <div>
            <label htmlFor="hook-url" className="block text-sm font-medium text-ink dark:text-cloud mb-1">Endpoint URL</label>
            <input id="hook-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/webhooks/tango" className={INPUT} />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setCreating(false)} className={`text-sm ${MUTED} hover:text-ink dark:hover:text-cloud transition`}>Cancel</button>
            <button type="submit" disabled={busy} className={PRIMARY_BTN}>{busy ? "Adding…" : "Add endpoint"}</button>
          </div>
        </form>
      )}

      {hooks === null ? (
        <p className={`text-sm ${MUTED}`}>Loading…</p>
      ) : hooks.length === 0 ? (
        <p className={`text-sm ${MUTED}`}>No endpoints yet. Add one to start receiving scan events.</p>
      ) : (
        <ul className="divide-y divide-[color:var(--color-border)] dark:divide-storm">
          {hooks.map((h) => (
            <li key={h.id} className="py-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-mono text-sm text-ink dark:text-cloud truncate">{h.url}</div>
                <div className={`text-xs ${MUTED}`}>
                  {h.enabled ? <span className="text-voltage-dark dark:text-voltage-light">Enabled</span> : <span className="text-fail">Disabled</span>}
                  {h.consecutive_failures > 0 ? ` · ${h.consecutive_failures} recent failure${h.consecutive_failures === 1 ? "" : "s"}` : ""}
                  {h.last_delivery_at ? ` · last ${formatDate(h.last_delivery_at)}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button type="button" onClick={() => toggle(h)} className={GHOST_BTN}>{h.enabled ? "Disable" : "Enable"}</button>
                <button type="button" onClick={() => remove(h.id)} className="border border-fail text-fail rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-fail/5 transition">
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
